import { useCallback, useEffect, useMemo, useState } from 'react';

import type { SerialRow, SerialRowMap } from './cartTypes';
import { cartItemKey } from './cartItemKey';
import { isCrossgradeSameHostBlocked } from './cartCrossgrade';
import { isCrossgradeProduct, isUpgradeItem } from './cartLineKinds';
import {
  buildSerialMapPatchBody,
  rowHasSerialOrHost,
} from './upgradeSerialMapPayload';

type UpdateCartItemArg = {
  cartItemId: string;
  quantity?: number;
  platform?: string;
  hostApplication?: any;
  serialMap?: any[];
};

type MutationTrigger<TArg> = (arg: TArg) => { unwrap: () => Promise<any> };

export function useUpgradeSerialRows({
  cart,
  updateCartItem,
  validateUpgradeSerial,
  validateCrossgradeSerial,
}: {
  cart: any;
  updateCartItem: (arg: UpdateCartItemArg) => Promise<any>;
  validateUpgradeSerial: MutationTrigger<{
    serial: string;
    productId: any;
  }>;
  validateCrossgradeSerial: MutationTrigger<{
    serial: string;
    productId: any;
    targetHostAppId?: string;
  }>;
}) {
  const [serialRowsByItem, setSerialRowsByItem] = useState<SerialRowMap>({});

  useEffect(() => {
    if (!cart?.items) return;

    setSerialRowsByItem((prev) => {
      const next: SerialRowMap = { ...prev };

      cart.items.forEach((item: any) => {
        if (!isUpgradeItem(item)) return;

        const key = cartItemKey(item);
        delete next[key];

        const isCrossgrade = isCrossgradeProduct(item.productId);

        const serialMap = item.serialMap ?? [];
        const serials = item.serials ?? [];

        let rows: SerialRow[] = [];

        if (Array.isArray(serialMap) && serialMap.length > 0) {
          rows = serialMap.map((entry: any) => {
            if (isCrossgrade) {
              return {
                serial: entry.serial || '',
                status: entry.serial ? 'valid' : 'idle',
                error: undefined,

                sourceHostApp: entry.sourceHostApp
                  ? { ...entry.sourceHostApp }
                  : null,

                targetHostApp: entry.targetHostApp
                  ? { ...entry.targetHostApp }
                  : null,

                validatedOnce: !!entry.serial,
              };
            }

            return {
              serial: entry.serial || '',
              status: entry.serial ? 'valid' : 'idle',
              error: undefined,

              hostApp: entry.hostApp ? { ...entry.hostApp } : null,

              validatedOnce: !!entry.serial,
            };
          });
        } else if (Array.isArray(serials) && serials.length > 0) {
          rows = serials.map((s: string) => ({
            serial: s,
            status: s ? 'valid' : 'idle',
            error: undefined,
          }));
        } else {
          const count = Math.max(1, item.quantity || 1);
          rows = Array.from({ length: count }, () => ({
            serial: '',
            status: 'idle' as const,
            error: undefined,
          }));
        }

        next[key] = rows;
      });

      return next;
    });
  }, [cart]);

  const allSerialsFlat = useMemo(
    () =>
      Object.entries(serialRowsByItem).flatMap(([itemId, rows]) =>
        rows.map((r, idx) => ({
          itemId,
          index: idx,
          serial: (r.serial || '').trim().toLowerCase(),
        })),
      ),
    [serialRowsByItem],
  );

  const hasGlobalDuplicate = useCallback(
    (currentItemId: string, rowIndex: number, serial: string) => {
      const norm = serial.trim().toLowerCase();
      if (!norm) return false;

      return allSerialsFlat.some(
        (entry) =>
          Boolean(entry.serial) &&
          (entry.itemId !== currentItemId || entry.index !== rowIndex) &&
          entry.serial === norm,
      );
    },
    [allSerialsFlat],
  );

  const commitUpgradeSerials = useCallback(
    (item: any, rows: SerialRow[]) => {
      updateCartItem({
        cartItemId: cartItemKey(item),
        serialMap: buildSerialMapPatchBody(rows, item),
      });
    },
    [updateCartItem],
  );

  const validateSerialForItem = useCallback(
    async (
      item: any,
      rowIndex: number,
      rawSerial: string,
      targetHostAppIdOverride?: string,
    ) => {
      const key = cartItemKey(item);
      const trimmed = rawSerial.trim();
      const isCrossgrade = isCrossgradeProduct(item.productId);

      if (!trimmed) {
        setSerialRowsByItem((prev) => {
          const rows = prev[key] || [];
          if (!rows[rowIndex]) return prev;

          const nextRows = [...rows];
          nextRows[rowIndex] = {
            ...nextRows[rowIndex],
            serial: rawSerial,
            status: 'idle',
            error: undefined,
            validatedOnce: false,
          };

          return { ...prev, [key]: nextRows };
        });
        return;
      }

      if (!isCrossgrade && hasGlobalDuplicate(key, rowIndex, trimmed)) {
        setSerialRowsByItem((prev) => {
          const rows = prev[key] || [];
          if (!rows[rowIndex]) return prev;

          const nextRows = [...rows];
          nextRows[rowIndex] = {
            ...nextRows[rowIndex],
            serial: rawSerial,
            status: 'invalid',
            error: 'This serial number is already used in this order.',
          };

          return { ...prev, [key]: nextRows };
        });
        return;
      }

      setSerialRowsByItem((prev) => {
        const rows = prev[key] || [];
        if (!rows[rowIndex]) return prev;

        const nextRows = [...rows];
        nextRows[rowIndex] = {
          ...nextRows[rowIndex],
          serial: rawSerial,
          status: 'loading',
          error: undefined,
        };

        return { ...prev, [key]: nextRows };
      });

      try {
        const response = isCrossgrade
          ? await validateCrossgradeSerial({
              serial: trimmed,
              productId: item.productId._id || item.productId,
              targetHostAppId:
                targetHostAppIdOverride ??
                serialRowsByItem[key]?.[rowIndex]?.targetHostApp?.id,
            }).unwrap()
          : await validateUpgradeSerial({
              serial: trimmed,
              productId: item.productId._id || item.productId,
            }).unwrap();

        if (!response?.valid) {
          setSerialRowsByItem((prev) => {
            const rows = prev[key] || [];
            if (!rows[rowIndex]) return prev;

            const nextRows = [...rows];
            nextRows[rowIndex] = {
              ...nextRows[rowIndex],
              status: 'invalid',
              error: response?.reason || 'Invalid serial.',
            };

            return { ...prev, [key]: nextRows };
          });
          return;
        }

        setSerialRowsByItem((prev) => {
          const rows = prev[key] || [];
          if (!rows[rowIndex]) return prev;

          const existingRow = rows[rowIndex];
          const nextRows = [...rows];

          const merged: SerialRow = {
            ...existingRow,
            status: 'valid',
            error: undefined,
            validatedOnce: true,

            ...(isCrossgrade
              ? {
                  sourceHostApp: response.hostApp
                    ? {
                        id: response.hostApp.id,
                        displayName: response.hostApp.displayName,
                        version: response.hostApp.version,
                      }
                    : null,

                  targetHostApp: existingRow.targetHostApp ?? null,
                }
              : {
                  hostApp:
                    existingRow.hostApp ??
                    (response.hostApp
                      ? {
                          id: response.hostApp.id,
                          displayName: response.hostApp.displayName,
                          version: response.hostApp.version,
                          ...(typeof response.hostApp.priceOverride === 'number'
                            ? { priceOverride: response.hostApp.priceOverride }
                            : {}),
                        }
                      : null),
                }),
          };

          if (isCrossgrade && isCrossgradeSameHostBlocked(merged)) {
            nextRows[rowIndex] = {
              ...merged,
              status: 'invalid',
              error: 'Crossgrade must be for a different host application.',
              validatedOnce: false,
            };
          } else {
            nextRows[rowIndex] = merged;
            commitUpgradeSerials(item, nextRows);
          }

          return { ...prev, [key]: nextRows };
        });
      } catch {
        setSerialRowsByItem((prev) => {
          const rows = prev[key] || [];
          if (!rows[rowIndex]) return prev;

          const nextRows = [...rows];
          nextRows[rowIndex] = {
            ...nextRows[rowIndex],
            status: 'invalid',
            error: 'Could not validate serial. Please try again.',
          };

          return { ...prev, [key]: nextRows };
        });
      }
    },
    [
      commitUpgradeSerials,
      hasGlobalDuplicate,
      serialRowsByItem,
      validateCrossgradeSerial,
      validateUpgradeSerial,
    ],
  );

  const removeSerialRow = useCallback(
    (item: any, rowIndex: number) => {
      const key = cartItemKey(item);
      const isCrossgrade = isCrossgradeProduct(item.productId);

      let nextRows: SerialRow[] = [];

      setSerialRowsByItem((prev) => {
        const rows = prev[key] || [];
        if (!rows.length) return prev;

        if (rows.length === 1) {
          nextRows = [
            isCrossgrade
              ? {
                  serial: '',
                  status: 'idle',
                  error: undefined,
                  sourceHostApp: null,
                  targetHostApp: null,
                  validatedOnce: false,
                }
              : {
                  serial: '',
                  status: 'idle',
                  error: undefined,
                  hostApp: null,
                  validatedOnce: false,
                },
          ];
        } else {
          nextRows = rows.filter((_, idx) => idx !== rowIndex);
        }

        return { ...prev, [key]: nextRows };
      });

      commitUpgradeSerials(item, nextRows);
    },
    [commitUpgradeSerials],
  );

  const handleUpgradeQuantityChange = useCallback(
    async (item: any, quantity: number) => {
      const safeQuantity = Math.max(1, quantity);
      const isCrossgrade = isCrossgradeProduct(item.productId);
      const key = cartItemKey(item);

      const existingRows = serialRowsByItem[key] || [];
      let nextRows = [...existingRows];

      if (safeQuantity > nextRows.length) {
        const diff = safeQuantity - nextRows.length;

        for (let i = 0; i < diff; i += 1) {
          nextRows.push(
            isCrossgrade
              ? {
                  serial: '',
                  status: 'idle',
                  error: undefined,
                  sourceHostApp: existingRows[0]?.sourceHostApp ?? null,
                  targetHostApp: existingRows[0]?.targetHostApp ?? null,
                  validatedOnce: false,
                }
              : {
                  serial: '',
                  status: 'idle',
                  error: undefined,
                  hostApp: existingRows[0]?.hostApp ?? null,
                  validatedOnce: false,
                },
          );
        }
      } else if (safeQuantity < nextRows.length) {
        nextRows = nextRows.slice(0, safeQuantity);
      }

      setSerialRowsByItem((prev) => ({ ...prev, [key]: nextRows }));

      const hasNewSerial = nextRows.some(
        (r: any, idx: number) =>
          r.serial?.trim() && !existingRows[idx]?.serial?.trim(),
      );

      const anyPatchableRow = nextRows.some((r: any) =>
        rowHasSerialOrHost(r, item),
      );

      if (
        !hasNewSerial &&
        safeQuantity > existingRows.length &&
        !anyPatchableRow
      ) {
        return;
      }

      await updateCartItem({
        cartItemId: cartItemKey(item),
        serialMap: buildSerialMapPatchBody(nextRows, item),
      });
    },
    [serialRowsByItem, updateCartItem],
  );

  return {
    serialRowsByItem,
    setSerialRowsByItem,
    validateSerialForItem,
    removeSerialRow,
    commitUpgradeSerials,
    handleUpgradeQuantityChange,
  };
}
